import { AlertOutlined } from '@ant-design/icons';
import { SystemRoleName } from '@evimo/types/enums/system-role-name';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthGuard } from '~/app/auth/guard/AuthGuard';
import { PageTabs } from '~/app/components/common/PageTabs/PageTabs';
import { ErrorManagementPage } from '~/app/components/errorManagement/ErrorManagementPage/ErrorManagementPage';
import { UnresolvedVendorErrorCodeTab } from '~/app/components/errorManagement/UnresolvedVendorErrorCodeTab/UnresolvedVendorErrorCodeTab';
import { useUnresolvedVendorErrorCodeTab } from '~/app/components/errorManagement/UnresolvedVendorErrorCodeTab/hooks/useUnresolvedVendorErrorCodeTab';
import { useErrorManagementPage } from '~/app/landing/admin/error-management/hooks/useErrorManagementPage';

const HEADER_ICON_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  borderRadius: 12,
  background: 'color-mix(in srgb, var(--evimo-blue) 12%, transparent)',
  color: 'var(--evimo-blue)',
  fontSize: 20,
} as const;

type ErrorManagementTabKey = 'catalog' | 'unresolved';

function CatalogTabPanel() {
  const page = useErrorManagementPage();
  return <ErrorManagementPage {...page} hidePageHeader />;
}

function UnresolvedTabPanel() {
  const page = useUnresolvedVendorErrorCodeTab();
  return <UnresolvedVendorErrorCodeTab {...page} />;
}

function ErrorManagementPageContent() {
  const { t } = useTranslation('errorManagement');
  const [activeTab, setActiveTab] = useState<ErrorManagementTabKey>('catalog');

  const tabItems = useMemo(
    () => [
      {
        key: 'catalog',
        label: t('tabs.catalog'),
        children: <CatalogTabPanel />,
      },
      {
        key: 'unresolved',
        label: t('tabs.unresolved'),
        children: <UnresolvedTabPanel />,
      },
    ],
    [t],
  );

  return (
    <div className="app-page">
      <PageTabs
        icon={
          <span style={HEADER_ICON_STYLE} aria-hidden>
            <AlertOutlined />
          </span>
        }
        title={t('title')}
        description={t('description')}
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as ErrorManagementTabKey)}
        items={tabItems}
      />
    </div>
  );
}

export function ErrorManagementPageContainer() {
  return (
    <AuthGuard requiredRole={SystemRoleName.SUPER_ADMIN}>
      <ErrorManagementPageContent />
    </AuthGuard>
  );
}
